import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { VehicleMaintenanceDto } from "../../../models/vehicleMaintenance/vehicleMaintenance";
import { VehicleMaintenanceReminderDto } from "../../../models/vehicleMaintenance/vehicleMaintenanceReminder";
import * as vehicleMaintenanceService from "../../../services/vehicleMaintenanceService";
import * as vehicleMaintenanceReminderService from "../../../services/vehicleMaintenanceReminderService";

export interface VehicleMaintenanceState {
    vehicleId: string | null;
    items: VehicleMaintenanceDto[];
    loading: boolean;
}

const initialState: VehicleMaintenanceState = {
    vehicleId: null,
    items: [],
    loading: false,
};

export const vehicleMaintenanceSlice = createSlice({
    name: "vehicleMaintenance",
    initialState,
    reducers: {
        setVehicleMaintenanceVehicleId: (state, action: PayloadAction<string | null>) => {
            state.vehicleId = action.payload;
        },
        setVehicleMaintenances: (state, action: PayloadAction<VehicleMaintenanceDto[]>) => {
            state.items = action.payload;
        },
        setVehicleMaintenancesLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        upsertVehicleMaintenance: (state, action: PayloadAction<VehicleMaintenanceDto>) => {
            const index = state.items.findIndex((i) => i.id === action.payload.id);
            if (index >= 0) {
                state.items[index] = action.payload;
            } else {
                state.items.unshift(action.payload);
            }
        },
        setVehicleMaintenanceReminder: (
            state,
            action: PayloadAction<{ vehicleMaintenanceId: string; reminder: VehicleMaintenanceReminderDto }>
        ) => {
            const index = state.items.findIndex((i) => i.id === action.payload.vehicleMaintenanceId);
            if (index < 0) return;

            state.items[index] = {
                ...state.items[index],
                reminder: action.payload.reminder,
            };
        },
    },
});

export const {
    setVehicleMaintenanceVehicleId,
    setVehicleMaintenances,
    setVehicleMaintenancesLoading,
    upsertVehicleMaintenance,
    setVehicleMaintenanceReminder,
} = vehicleMaintenanceSlice.actions;

export const fetchVehicleMaintenancesForVehicle = createAsyncThunk(
    "vehicleMaintenance/fetchForVehicle",
    async(vehicleId: string, thunkApi) => {
        thunkApi.dispatch(setVehicleMaintenancesLoading(true));
        thunkApi.dispatch(setVehicleMaintenanceVehicleId(vehicleId));

        const res = await vehicleMaintenanceService.getVehicleMaintenances(
            {
                vehicleIds: [vehicleId],
            },
            thunkApi.signal
        );

        thunkApi.dispatch(setVehicleMaintenances(res.data ?? []));
        thunkApi.dispatch(setVehicleMaintenancesLoading(false));
    }
);

export const fetchVehicleMaintenanceById = createAsyncThunk(
    "vehicleMaintenance/fetchById",
    async(id: string, thunkApi) => {
        const res = await vehicleMaintenanceService.getVehicleMaintenance(id, thunkApi.signal);
        thunkApi.dispatch(upsertVehicleMaintenance(res.data));
    }
);

export const saveVehicleMaintenanceThunk = createAsyncThunk(
    "vehicleMaintenance/save",
    async(dto: VehicleMaintenanceDto, thunkApi) => {
        const res = await vehicleMaintenanceService.saveVehicleMaintenance(dto, thunkApi.signal);
        thunkApi.dispatch(upsertVehicleMaintenance(res.data));
    }
);

export const markVehicleMaintenanceDone = createAsyncThunk(
    "vehicleMaintenance/markDone",
    async (vehicleMaintenanceId: string, thunkApi) => {
        const res = await vehicleMaintenanceReminderService.markMaintenanceDone(vehicleMaintenanceId, thunkApi.signal);
        thunkApi.dispatch(setVehicleMaintenanceReminder({ vehicleMaintenanceId, reminder: res.data }));
        return res.data;
    }
);

export const vehicleMaintenanceReducer = vehicleMaintenanceSlice.reducer;
